"use client"

import { useState } from "react"
import { Head, useForm } from "@inertiajs/react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { toast } from 'sonner'
import {
  Globe,
  User,
  Key,
  Shield,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Save,
  Trash2,
  BarChart3,
  ExternalLink,
  Server,
} from "lucide-react"
import DashboardLayout from "@/layout/dashboard-layout"
import { router } from "@inertiajs/react"

interface Client {
  id: number
  name: string
}

interface Blog {
  id: number
  name: string
  domain: string
  active: boolean
  client_id: number
  client_name?: string
  posts_count: number
  views_count: number
  last_post_date?: string
  created_at: string
  updated_at: string
  // Campos de credenciais
  category_id?: number
  plataforma_id?: number
  usuario?: string
  senha?: string
  token?: string
}

interface Category {
  id: number
  name: string
  color: string
}

interface Plataforma {
  id: number
  name: string
  icon: string
  requires_token: boolean
}

interface Props {
  blog: Blog
  clients: Client[]
  categories: Category[]
  plataformas: Plataforma[]
  errors?: string[]
}

const grupos = [
  { id: "tech", name: "Tecnologia", color: "bg-blue-500" },
  { id: "lifestyle", name: "Lifestyle", color: "bg-pink-500" },
  { id: "business", name: "Negócios", color: "bg-green-500" },
  { id: "health", name: "Saúde", color: "bg-purple-500" },
  { id: "education", name: "Educação", color: "bg-orange-500" },
]

const plataformas = [
  { id: "wordpress", name: "WordPress", icon: "🔷" },
  { id: "blogger", name: "Blogger", icon: "🟠" },
  { id: "medium", name: "Medium", icon: "⚫" },
  { id: "ghost", name: "Ghost", icon: "👻" },
  { id: "substack", name: "Substack", icon: "🟡" },
]

export default function EditBlogPage({ blog, clients, categories, plataformas, errors }: Props) {

  const { data, setData, put, delete: destroy, processing } = useForm({
    name: blog?.name || "",
    domain: blog?.domain || "",
    client_id: blog?.client_id || "",
    active: blog?.active !== undefined ? blog.active : true,
    // Campos de credenciais agora persistidos
    category_id: blog?.category_id || "",
    plataforma_id: blog?.plataforma_id || "",
    usuario: blog?.usuario || "",
    senha: blog?.senha || "",
    token: blog?.token || "",
    // Campos para compatibilidade com UI v0
    grupo: blog?.category_id || "tech",
    plataforma: blog?.plataforma_id || "wordpress"
  })

  const handleInputChange = (field: string, value: string | number | boolean) => {
    // Todos os campos agora vão para o backend via setData
    setData(field as any, value)
  }

  const handleSaveBlog = () => {
    put(`/dashboard/blogs/${blog.id}`, {
      onSuccess: () => {
        toast.success('Blog atualizado com sucesso!')
      },
      onError: (errors) => {
        console.error('Erro ao atualizar blog:', errors)
        toast.error('Erro ao atualizar blog')
      }
    })
  }

  const handleDeleteBlog = () => {
    if (confirm("Tem certeza que deseja remover este blog do sistema? Esta ação não pode ser desfeita.")) {
      destroy(`/dashboard/blogs/${blog.id}`)
    }
  }

  const handleTestConnection = async () => {
    alert("Testando conexão... (Funcionalidade simulada)")
  }

  const handleBack = () => {
    router.visit("/dashboard/blogs")
  }

  const isFormValid = data.name && data.domain && data.client_id && data.grupo && data.usuario && data.senha

  const getGrupoInfo = (grupoId: string) => {
    return grupos.find((g) => g.id === grupoId)
  }

  const getPlataformaInfo = (plataformaId: string) => {
    return plataformas.find((p) => p.id === plataformaId)
  }

  return (
    <DashboardLayout>
      <Head title="Editar Blog" />
      
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Editar Blog</h1>
            <p className="text-muted-foreground">Configure as informações do blog no sistema Memoráveis</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => window.open(`https://${data.domain}`, "_blank")}
              className="bg-transparent"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Visitar Site
            </Button>
            <Button variant="outline" className="bg-transparent">
              <BarChart3 className="mr-2 h-4 w-4" />
              Estatísticas
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Formulário Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informações Básicas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Informações do Blog
                </CardTitle>
                <CardDescription>Configure as informações básicas do blog</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome do Blog *</Label>
                    <Input
                      id="nome"
                      value={data.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      placeholder="Ex: Blog de Tecnologia"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dominio">Domínio *</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                        https://
                      </span>
                      <Input
                        id="dominio"
                        value={data.domain}
                        onChange={(e) => handleInputChange("domain", e.target.value)}
                        placeholder="exemplo.com"
                        className="rounded-l-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Status *</Label>
                    <Select value={data.active ? "ativo" : "inativo"} onValueChange={(value) => handleInputChange("active", value === "ativo")}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ativo">Ativo</SelectItem>
                        <SelectItem value="inativo">Inativo</SelectItem>
                        <SelectItem value="manutencao">Em Manutenção</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Grupo *</Label>
                    <Select value={data.grupo} onValueChange={(value) => handleInputChange("grupo", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um grupo" />
                      </SelectTrigger>
                      <SelectContent>
                        {grupos.map((grupo) => (
                          <SelectItem key={grupo.id} value={grupo.id}>
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${grupo.color}`}></div>
                              {grupo.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <Select value={data.client_id?.toString() || ""} onValueChange={(value) => handleInputChange("client_id", parseInt(value))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id.toString()}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo/Plataforma *</Label>
                    <Select value={data.plataforma} onValueChange={(value) => handleInputChange("plataforma", value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {plataformas.map((plataforma) => (
                          <SelectItem key={plataforma.id} value={plataforma.id}>
                            <div className="flex items-center gap-2">
                              <span>{plataforma.icon}</span>
                              {plataforma.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Credenciais de Acesso */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Credenciais de Acesso
                </CardTitle>
                <CardDescription>Configure as credenciais para acessar o blog</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="usuario">Usuário *</Label>
                    <Input
                      id="usuario"
                      value={data.usuario}
                      onChange={(e) => handleInputChange("usuario", e.target.value)}
                      placeholder="Nome de usuário ou email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="senha">Senha *</Label>
                    <Input
                      id="senha"
                      type="password"
                      value={data.senha}
                      onChange={(e) => handleInputChange("senha", e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="token">Token de API (Opcional)</Label>
                  <Input
                    id="token"
                    value={data.token}
                    onChange={(e) => handleInputChange("token", e.target.value)}
                    placeholder="Token para acesso via API (se disponível)"
                  />
                  <p className="text-xs text-muted-foreground">
                    Token necessário para algumas plataformas como WordPress.com, Medium, etc.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Informações da Plataforma */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  Informações da Plataforma
                </CardTitle>
                <CardDescription>Detalhes específicos da plataforma selecionada</CardDescription>
              </CardHeader>
              <CardContent>
                {data.plataforma === "wordpress" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Suporte completo para WordPress REST API</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Publicação automática de posts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Gerenciamento de categorias e tags</span>
                    </div>
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Para WordPress, certifique-se de que o plugin REST API está ativo e as credenciais têm permissões
                        de publicação.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                {data.plataforma === "medium" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Publicação via Medium API</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                      <span>Token de API obrigatório</span>
                    </div>
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Para Medium, o token de API é obrigatório. Obtenha em: Settings → Integration tokens
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                {data.plataforma === "blogger" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Integração com Google Blogger API</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                      <span>Autenticação via Google OAuth</span>
                    </div>
                  </div>
                )}

                {(data.plataforma === "ghost" || data.plataforma === "substack") && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Suporte via API da plataforma</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                      <span>Configuração específica necessária</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Zona de Perigo */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-5 w-5" />
                  Zona de Perigo
                </CardTitle>
                <CardDescription>Ações irreversíveis para este blog</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Remover o blog do sistema irá excluir todas as informações associadas. Esta ação não pode ser desfeita.
                    </AlertDescription>
                  </Alert>
                  <Button variant="destructive" onClick={handleDeleteBlog}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Remover Blog do Sistema
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Painel Lateral */}
          <div className="space-y-6">
            {/* Informações do Blog */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Informações do Blog
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium">Adicionado em:</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(blog.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Total de Posts:</p>
                  <p className="text-sm text-muted-foreground">{blog.posts_count}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Última Publicação:</p>
                  <p className="text-sm text-muted-foreground">
                    {blog.last_post_date ? new Date(blog.last_post_date).toLocaleDateString('pt-BR') : 'Nenhuma'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Status:</p>
                  <Badge variant={blog.active ? "default" : "secondary"}>
                    {blog.active ? "ativo" : "inativo"}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Status da Configuração */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Status da Configuração
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  {data.name && data.domain ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="text-sm">Informações básicas</span>
                </div>
                <div className="flex items-center gap-2">
                  {data.grupo ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="text-sm">Grupo selecionado</span>
                </div>
                <div className="flex items-center gap-2">
                  {data.plataforma ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="text-sm">Plataforma definida</span>
                </div>
                <div className="flex items-center gap-2">
                  {data.usuario && data.senha ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="text-sm">Credenciais configuradas</span>
                </div>
              </CardContent>
            </Card>

            {/* Resumo */}
            <Card>
              <CardHeader>
                <CardTitle>Resumo da Configuração</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium">Nome:</p>
                  <p className="text-sm text-muted-foreground">{data.name || "Não definido"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">URL:</p>
                  <p className="text-sm text-muted-foreground">
                    {data.domain ? `https://${data.domain}` : "Não definida"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Grupo:</p>
                  <div className="flex items-center gap-2">
                    {data.grupo && getGrupoInfo(data.grupo) && (
                      <>
                        <div className={`w-3 h-3 rounded-full ${getGrupoInfo(data.grupo)?.color}`}></div>
                        <span className="text-sm text-muted-foreground">{getGrupoInfo(data.grupo)?.name}</span>
                      </>
                    )}
                    {!data.grupo && <span className="text-sm text-muted-foreground">Não selecionado</span>}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium">Plataforma:</p>
                  <div className="flex items-center gap-2">
                    {getPlataformaInfo(data.plataforma) && (
                      <>
                        <span>{getPlataformaInfo(data.plataforma)?.icon}</span>
                        <span className="text-sm text-muted-foreground">
                          {getPlataformaInfo(data.plataforma)?.name}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ações */}
            <div className="space-y-3">
              <Button className="w-full h-12" onClick={handleSaveBlog} disabled={!isFormValid || processing}>
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvar Alterações
                  </>
                )}
              </Button>
              <Button variant="outline" className="w-full h-12 bg-transparent" onClick={handleTestConnection}>
                <Key className="mr-2 h-4 w-4" />
                Testar Conexão
              </Button>
            </div>

            {!isFormValid && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>Preencha todos os campos obrigatórios (*) para continuar.</AlertDescription>
              </Alert>
            )}

            {errors && errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
